import { isEmpty, isNil } from 'lodash';
import { CSSProperties, ComponentType, useState } from 'react';
import ReactSelect, { ActionMeta, GroupBase, InputActionMeta, MultiValue } from 'react-select';
import { FilterTagOption } from '@shared/components/filter/types';

declare module 'react-select/dist/declarations/src/Select' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
    hideMultiValue?: boolean;
    hasServerMore?: boolean;
    onLoadMore?: () => void;
  }
}
import { MBTextBody1 } from '@shared/components/typography';
import { Colors, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import styles from '../styles.module.css';
import {
  Control,
  IndicatorsContainer,
  Input,
  Menu,
  MenuList,
  MultiValueRemove,
  Placeholder,
  ValueContainer,
} from './ReactSelectComponents';

type DefaultOption = FilterTagOption;

interface OpenViewProps<Option extends DefaultOption = DefaultOption> {
  componentId: string;
  options: Option[];
  selectedOptions?: Option[];
  onSearch?(keyword: string): void;
  onChange(newValue: MultiValue<Option>, actionMeta: ActionMeta<Option>): void;
  components: any;
  hideSelectedOptions?: boolean;
  isMultiSelect?: boolean;
  hideMultiValueAbove?: number;
  onLoadMore?(): void;
  hasServerMore?: boolean;
}

function HiddenMultiValue() {
  return null;
}

export function OpenView<Option extends DefaultOption = DefaultOption>({
  componentId,
  options,
  selectedOptions = [],
  onSearch = () => {},
  onChange = () => {},
  components = {},
  hideSelectedOptions = false,
  isMultiSelect = true,
  hideMultiValueAbove,
  onLoadMore,
  hasServerMore = false,
}: OpenViewProps<Option>) {
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = (val: string, opt: InputActionMeta) => {
    if (opt.action === 'input-change') {
      setInputValue(val);
      onSearch(val);
    }
  };

  const shouldHideMultiValue = !isNil(hideMultiValueAbove) && options.length > hideMultiValueAbove;

  return (
    <ReactSelect<Option, true, GroupBase<Option>>
      options={options}
      defaultValue={selectedOptions}
      autoFocus
      isMulti={isMultiSelect ? true : undefined}
      isClearable={false}
      menuIsOpen
      hideSelectedOptions={hideSelectedOptions}
      closeMenuOnSelect={false}
      closeMenuOnScroll={false}
      key={`select-${componentId}`}
      name={`select-${componentId}`}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={onChange}
      {...(shouldHideMultiValue ? { hideMultiValue: true } : {})}
      {...(hasServerMore || onLoadMore ? { hasServerMore, onLoadMore } : {})}
      // options https://react-select.com/components
      components={{
        Menu,
        MenuList,
        MultiValueRemove,
        Control,
        IndicatorsContainer,
        Placeholder,
        Input,
        ValueContainer,
        ...components,
        ...(shouldHideMultiValue ? { MultiValue: HiddenMultiValue } : {}),
      }}
      unstyled
      styles={{
        // because `unstyled` doesn't do everything
        input: () => ({}),
      }}
      classNames={{
        input: () => styles.searchInput,
      }}
      minMenuHeight={150}
      maxMenuHeight={Math.max(window.innerHeight * 0.2, 300)}
    />
  );
}

export function ClosedView<Option extends DefaultOption = DefaultOption>({
  selections,
  ItemComponent,
  maxWidth,
}: {
  selections: Option[];
  ItemComponent: ComponentType<{ selection: Option }>;
  maxWidth?: number;
}) {
  if (isEmpty(selections)) {
    return (
      <div style={{ paddingLeft: grid(1) }}>
        <MBTextBody1 color={Colors.v2.gray50}>{i18n('common.selectOptions')}</MBTextBody1>
      </div>
    );
  }

  return (
    <div
      className={styles.closedView}
      style={{ '--max-width': !isNil(maxWidth) ? `${maxWidth}px` : undefined } as CSSProperties}
    >
      {ItemComponent &&
        selections?.map(selection => (
          <ItemComponent key={selection.value as string | number} selection={selection} />
        ))}
      {!ItemComponent && selections?.map(selection => selection.label).join(', ')}
    </div>
  );
}
